import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, BookOpen, TrendingUp, ChevronRight, Loader2 } from "lucide-react";

function Stat({ Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-[0_8px_30px_rgba(37,75,54,0.06)]">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </span>
      <p className="mt-3 font-heading text-3xl font-extrabold tracking-tight" data-testid={testid}>
        {value}
      </p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (data === null)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  if (data === false) return <p className="py-20 text-center text-muted-foreground">Could not load dashboard.</p>;

  const { staff, modules } = data;
  const avg = staff.length ? Math.round(staff.reduce((s, r) => s + r.completion_pct, 0) / staff.length) : 0;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Admin</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight">Training dashboard</h1>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat Icon={Users} label="Staff members" value={staff.length} testid="stat-staff" />
        <Stat Icon={BookOpen} label="Training modules" value={modules.length} testid="stat-modules" />
        <Stat Icon={TrendingUp} label="Avg. completion" value={`${avg}%`} testid="stat-avg" />
      </div>

      <div className="rounded-2xl border border-border bg-card shadow-[0_8px_30px_rgba(37,75,54,0.06)]">
        <div className="border-b border-border px-5 py-4">
          <h2 className="font-heading text-lg font-bold tracking-tight">Employee progress</h2>
          <p className="text-sm text-muted-foreground">Tap an employee to view their full training history.</p>
        </div>

        {staff.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-muted-foreground">
            No staff accounts yet. Add staff from the Staff tab.
          </p>
        ) : (
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  {modules.map((m) => (
                    <TableHead key={m.id} className="text-center">
                      {m.title}
                    </TableHead>
                  ))}
                  <TableHead className="text-right">Completion</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((s) => (
                  <TableRow
                    key={s.user_id}
                    data-testid={`admin-employee-row-${s.user_id}`}
                    onClick={() => navigate(`/admin/employee/${s.user_id}`)}
                    className="cursor-pointer"
                  >
                    <TableCell>
                      <p className="font-semibold">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.email}</p>
                    </TableCell>
                    {s.modules.map((m) => (
                      <TableCell key={m.module_id} className="text-center">
                        <StatusBadge status={m.status} />
                        {(m.status === "Passed" ? m.final_score : m.latest_score) != null && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {m.status === "Passed" ? m.final_score : m.latest_score}%
                          </p>
                        )}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <span className="font-heading text-lg font-bold text-primary">{s.completion_pct}%</span>
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* mobile list */}
        <div className="divide-y divide-border md:hidden">
          {staff.map((s) => (
            <button
              key={s.user_id}
              data-testid={`admin-employee-card-${s.user_id}`}
              onClick={() => navigate(`/admin/employee/${s.user_id}`)}
              className="flex w-full items-center gap-3 px-5 py-4 text-left"
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{s.name}</p>
                <p className="truncate text-xs text-muted-foreground">{s.email}</p>
                <Progress value={s.completion_pct} className="mt-2 h-2 bg-secondary" />
              </div>
              <div className="text-right">
                <span className="font-heading text-lg font-bold text-primary">{s.completion_pct}%</span>
                <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
